import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../../contexts/UserContext';
import { fetchCounters, resetButtonCounts } from '../../../services/api';
import socketService from '../../../services/socketService';
import { config } from '../../../config';
import { useToast } from '../../../utils/toastUtils';
import ToastContainer from '../../common/ToastContainer';
import { isCounterExpired } from '../../../utils/dateUtils';

// Import types
import { 
  PerformanceMetrics, 
  TestConfig, 
  TestSuiteConfig, 
  TestHistoryEntry, 
  SuiteProgress,
  Counter
} from './types';

// Import constants
import { ALL_TEST_OPTIONS } from './constants';

// Import components
import TestConfiguration from './TestConfiguration';
import TestSuiteConfiguration from './TestSuiteConfiguration';
import PerformanceMetricsComponent from './PerformanceMetrics';
import TestHistory from './TestHistory';

// Import utils
import {
  calculateMetrics,
  processBatch,
  resetMetricsState,
  resetThrottlingState,
  validateAndPrepareTestConfig,
  getTestCaseCount,
  getEstimatedTime
} from './TestLogic';

/**
 * Performance Testing Component
 * 
 * This component provides performance testing tools for counters, allowing users to:
 * 1. Run individual tests with specific parameters
 * 2. Run test suites with combinations of batch sizes and intervals
 * 3. Collect and analyze performance metrics
 * 
 * Both individual tests and test suites automatically reset button counts to ensure 
 * consistent baseline measurements across test runs.
 */
const Performance: React.FC = () => {
  const { t } = useTranslation('performance');
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const { showToast, toasts } = useToast();
  
  // State for counters, test configuration, and metrics
  const [counters, setCounters] = useState<Counter[]>([]);
  const [loading, setLoading] = useState(true);
  const [testConfig, setTestConfig] = useState<TestConfig>({
    counterId: '',
    clicksPerSecond: 10,
    testDuration: 10,
    batchSize: config.batching.size,
    batchInterval: config.batching.interval
  });
  const [isRunningTest, setIsRunningTest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null);
  const [testHistory, setTestHistory] = useState<TestHistoryEntry[]>([]);
  
  // Test Suite configuration state
  const [testSuiteConfig, setTestSuiteConfig] = useState<TestSuiteConfig>({
    selectedBatchSizes: [],
    selectedIntervals: []
  });
  const [isRunningSuite, setIsRunningSuite] = useState(false);
  const [currentSuiteProgress, setCurrentSuiteProgress] = useState<SuiteProgress | null>(null);
  
  // Refs for test tracking
  const testIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const testStartTimeRef = useRef<number>(0);
  const clickCountRef = useRef<number>(0);
  const failedClicksRef = useRef<number>(0);
  const responseTimesRef = useRef<number[]>([]);
  const isRunningSuiteRef = useRef<boolean>(false);
  
  // Redirect non-admin users
  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
    }
  }, [isAdmin, navigate]);
  
  // Fetch available counters
  useEffect(() => {
    const getCounters = async () => {
      try {
        setLoading(true);
        const fetchedCounters = await fetchCounters();
        
        // Filter out expired counters using the shared utility
        const activeCounters = fetchedCounters.filter(counter => !isCounterExpired(counter));
        
        // Sort counters by creation date (newest first)
        const sortedCounters = [...activeCounters].sort((a, b) => {
          // Assuming counters have a createdAt field (common MongoDB pattern)
          // If dates aren't available, this will maintain the original order
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });
        
        setCounters(sortedCounters);
        
        // Set first counter as default if available
        if (sortedCounters.length > 0 && !testConfig.counterId) {
          setTestConfig(prev => ({
            ...prev,
            counterId: sortedCounters[0]._id
          }));
        }
        
        setError(null);
      } catch (err) {
        setError(t('errors.countersLoadFailed', 'Failed to load counters. Please try again.'));
        console.error('Error loading counters:', err);
      } finally {
        setLoading(false);
      }
    };
    
    getCounters();
  }, [t]);
  
  // Handle form input changes
  const handleConfigChange = (e: React.ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    
    // For counterId, just use the string value
    if (name === 'counterId') {
      setTestConfig(prev => ({
        ...prev,
        [name]: value
      }));
      return;
    }
    
    // For numeric fields, allow empty string temporarily during editing
    setTestConfig(prev => ({
      ...prev,
      [name]: value === '' ? '' : Number(value)
    }));
  };
  
  // Handle test suite configuration changes
  const handleTestSuiteConfigChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    
    if (name.startsWith('batchSize-')) {
      const batchSize = Number(name.replace('batchSize-', ''));
      
      setTestSuiteConfig(prev => ({
        ...prev,
        selectedBatchSizes: checked 
          ? [...prev.selectedBatchSizes, batchSize].sort((a, b) => a - b)
          : prev.selectedBatchSizes.filter(size => size !== batchSize)
      }));
    } else if (name.startsWith('interval-')) {
      const interval = Number(name.replace('interval-', ''));
      
      setTestSuiteConfig(prev => ({
        ...prev,
        selectedIntervals: checked 
          ? [...prev.selectedIntervals, interval].sort((a, b) => a - b)
          : prev.selectedIntervals.filter(int => int !== interval)
      }));
    }
  };
  
  // Start performance test
  const startTest = async () => {
    // Make sure we're not in suite mode
    isRunningSuiteRef.current = false;
    
    if (!testConfig.counterId) {
      setError(t('errors.selectCounter', 'Please select a counter first'));
      return;
    }
    
    if (!testConfig.clicksPerSecond || !testConfig.testDuration || !testConfig.batchSize || !testConfig.batchInterval) {
      setError(t('errors.allFields', 'Please fill in all fields'));
      return;
    }
    
    const validatedConfig = validateAndPrepareTestConfig(testConfig);
    
    try {
      setIsRunningTest(true);
      setError(null);
      resetMetricsState(clickCountRef, failedClicksRef, responseTimesRef, testStartTimeRef);
      
      // Reset throttling state before running the test
      await resetThrottlingState();
      
      // Reset button counts before running the test
      try {
        await resetButtonCounts(validatedConfig.counterId);
      } catch (resetError) {
        console.error('Error resetting button counts:', resetError);
        // Continue with the test even if reset fails
      }
      
      // Fetch buttons for the selected counter
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/counters/${validatedConfig.counterId}/buttons`);
      const buttonsData = await response.json();
      
      // Filter out buttons with a cost to tap
      const buttons = Array.isArray(buttonsData) 
        ? buttonsData.filter((button: any) => !button.costToClick || button.costToClick <= 0) 
        : [];
      
      if (!buttons || buttons.length === 0) {
        setError(t('errors.noButtons', 'Selected counter has no free buttons to test. All buttons have a cost to tap or counter has no buttons.'));
        setIsRunningTest(false);
        return;
      }
      
      // Set up test interval
      const buttonIds = buttons.map((button: any) => button._id);
      const clicksPerInterval = Math.ceil(validatedConfig.clicksPerSecond * (validatedConfig.batchInterval / 1000));
      
      testIntervalRef.current = setInterval(() => {
        // Generate simulated clicks
        const batchButtonIds: string[] = [];
        
        for (let i = 0; i < clicksPerInterval; i++) {
          const randomIndex = Math.floor(Math.random() * buttonIds.length);
          batchButtonIds.push(buttonIds[randomIndex]);
          clickCountRef.current++;
        }
        
        // Process the batch
        processBatch(
          batchButtonIds, 
          validatedConfig.counterId, 
          clickCountRef, 
          failedClicksRef, 
          responseTimesRef,
          testStartTimeRef,
          setMetrics
        );
        
        // Check if test duration has been reached
        const elapsedTime = (Date.now() - testStartTimeRef.current) / 1000;
        if (elapsedTime >= validatedConfig.testDuration) {
          stopTest();
        }
      }, validatedConfig.batchInterval);
      
    } catch (err) {
      setError(t('errors.testFailed', 'Failed to start test. Please try again.'));
      console.error('Error starting test:', err);
      setIsRunningTest(false);
    }
  };
  
  // Stop performance test
  const stopTest = () => {
    if (testIntervalRef.current) {
      clearInterval(testIntervalRef.current);
      testIntervalRef.current = null;
    }
    
    setIsRunningTest(false);
    
    // Calculate final metrics
    if (clickCountRef.current > 0) {
      const finalMetrics = calculateMetrics(
        clickCountRef,
        failedClicksRef,
        responseTimesRef,
        testStartTimeRef,
        typeof testConfig.batchSize === 'string' ? parseInt(testConfig.batchSize) : testConfig.batchSize
      );
      
      setMetrics(finalMetrics);
      
      // Add to test history
      const historyEntry: TestHistoryEntry = {
        id: Date.now().toString(),
        date: new Date(),
        timestamp: new Date().toISOString(),
        config: {
          counterId: testConfig.counterId,
          clicksPerSecond: testConfig.clicksPerSecond as number,
          testDuration: testConfig.testDuration as number,
          batchSize: testConfig.batchSize as number,
          batchInterval: testConfig.batchInterval as number
        },
        metrics: finalMetrics
      };
      
      setTestHistory(prev => [historyEntry, ...prev]);
    }
  };
  
  // Run test suite
  const runTestSuite = async (overrideConfig?: TestSuiteConfig) => {
    const configToUse = overrideConfig || testSuiteConfig;
    
    if (!testConfig.counterId) {
      setError(t('errors.selectCounter', 'Please select a counter first'));
      return;
    }
    
    // Add null/undefined checks before accessing length properties
    if (!configToUse || !configToUse.selectedBatchSizes || !configToUse.selectedIntervals || 
        configToUse.selectedBatchSizes.length === 0 || configToUse.selectedIntervals.length === 0) {
      setError(t('errors.selectBatchAndInterval', 'Please select at least one batch size and interval'));
      return;
    }
    
    try {
      isRunningSuiteRef.current = true;
      setIsRunningSuite(true);
      setError(null);
      
      // Create a copy of the test config for the suite
      const suiteTestConfig = { ...testConfig };
      
      // Calculate total number of test cases
      const totalCases = configToUse.selectedBatchSizes.length * configToUse.selectedIntervals.length;
      let completedCases = 0;
      const allResults: TestHistoryEntry[] = [];
      
      // Get all batch size and interval combinations
      const combinations: Array<{batchSize: number, interval: number}> = [];
      for (const batchSize of configToUse.selectedBatchSizes) {
        for (const interval of configToUse.selectedIntervals) {
          combinations.push({ batchSize, interval });
        }
      }
      
      // Set initial progress
      setCurrentSuiteProgress({
        current: 1,
        total: totalCases,
        batchSize: combinations[0].batchSize,
        interval: combinations[0].interval,
        totalCases,
        completedCases: 0,
        progress: 0,
        currentTest: {
          batchSize: combinations[0].batchSize,
          interval: combinations[0].interval
        }
      });
      
      // Run each test case sequentially
      for (const { batchSize, interval } of combinations) {
        // Skip if the suite has been canceled
        if (!isRunningSuiteRef.current) {
          break;
        }
        
        // Update current test info
        setCurrentSuiteProgress(prev => {
          if (!prev) return null;
          return {
            ...prev,
            currentTest: { batchSize, interval }
          };
        });
        
        // Set up test config for this iteration
        suiteTestConfig.batchSize = batchSize;
        suiteTestConfig.batchInterval = interval;
        
        // Run single test
        const validatedConfig = validateAndPrepareTestConfig(suiteTestConfig);
        
        // Reset metrics state for this test
        resetMetricsState(clickCountRef, failedClicksRef, responseTimesRef, testStartTimeRef);
        await resetThrottlingState();
        
        // Reset button counts before this test case
        try {
          await resetButtonCounts(validatedConfig.counterId);
        } catch (resetError) {
          console.error('Error resetting button counts:', resetError);
          // Continue with the test even if reset fails
        }
        
        try {
          // Fetch buttons for the selected counter
          const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/counters/${validatedConfig.counterId}/buttons`);
          const buttonsData = await response.json();
          
          // Filter out buttons with a cost to tap
          const buttons = Array.isArray(buttonsData) 
            ? buttonsData.filter((button: any) => !button.costToClick || button.costToClick <= 0) 
            : [];
          
          if (!buttons || buttons.length === 0) {
            console.error('No free buttons found for testing');
            setError(t('errors.noButtons', 'Selected counter has no free buttons to test. All buttons have a cost to tap or counter has no buttons.'));
            isRunningSuiteRef.current = false;
            setIsRunningSuite(false);
            return;
          }
          
          // Set up test for this case
          const buttonIds = buttons.map((button: any) => button._id);
          const clicksPerInterval = Math.ceil(validatedConfig.clicksPerSecond * (validatedConfig.batchInterval / 1000));
          
          // Create promise to run this test
          const runSingleTest = () => new Promise<void>((resolve) => {
            // Set up end time
            const testEndTime = Date.now() + (validatedConfig.testDuration * 1000);
            
            // Set up interval for this test case
            const intervalId = setInterval(() => {
              if (Date.now() >= testEndTime) {
                clearInterval(intervalId);
                
                // Calculate metrics for this test
                const testDuration = (Date.now() - testStartTimeRef.current) / 1000;
                const finalMetrics = calculateMetrics(
                  clickCountRef,
                  failedClicksRef,
                  responseTimesRef,
                  testStartTimeRef,
                  validatedConfig.batchSize
                );
                
                // Create history entry for this test
                const historyEntry: TestHistoryEntry = {
                  id: Date.now().toString(),
                  date: new Date(),
                  timestamp: new Date().toISOString(),
                  config: {
                    counterId: validatedConfig.counterId,
                    clicksPerSecond: validatedConfig.clicksPerSecond,
                    testDuration: validatedConfig.testDuration,
                    batchSize: validatedConfig.batchSize,
                    batchInterval: validatedConfig.batchInterval
                  },
                  metrics: finalMetrics,
                  tags: ['suite']
                };
                
                // Add to results
                allResults.push(historyEntry);
                
                // Update progress
                completedCases++;
                
                // Allow some time for any pending API calls to complete
                setTimeout(() => {
                  setCurrentSuiteProgress(prev => {
                    if (!prev) return null;
                    
                    // Calculate progress percentage (0-100)
                    const progress = Math.round((completedCases / totalCases) * 100);
                    
                    // Get the next test details if there are more tests
                    const nextTestIndex = completedCases;
                    const hasMoreTests = nextTestIndex < combinations.length;
                    const nextTest = hasMoreTests ? combinations[nextTestIndex] : null;
                    
                    return {
                      ...prev,
                      completedCases,
                      progress,
                      current: completedCases + 1, // Increment the current test counter
                      // Update batch size and interval to show the next test values
                      batchSize: nextTest ? nextTest.batchSize : prev.batchSize,
                      interval: nextTest ? nextTest.interval : prev.interval,
                      currentTest: nextTest || prev.currentTest
                    };
                  });
                  
                  resolve();
                }, 500);
              } else {
                // Generate simulated clicks for this interval
                const batchButtonIds: string[] = [];
                
                for (let i = 0; i < clicksPerInterval; i++) {
                  const randomIndex = Math.floor(Math.random() * buttonIds.length);
                  batchButtonIds.push(buttonIds[randomIndex]);
                  clickCountRef.current++;
                }
                
                // Process batch
                processBatch(
                  batchButtonIds, 
                  validatedConfig.counterId, 
                  clickCountRef, 
                  failedClicksRef, 
                  responseTimesRef,
                  testStartTimeRef,
                  setMetrics
                );
                
                // Animate progress for this test
                const animateProgress = () => {
                  const elapsedTime = (Date.now() - testStartTimeRef.current) / 1000;
                  const subProgress = Math.min(Math.round((elapsedTime / validatedConfig.testDuration) * 100), 100);
                  
                  setCurrentSuiteProgress(prev => {
                    if (!prev) return null;
                    
                    // Calculate overall progress including this test's sub-progress
                    const overallProgress = Math.round(
                      ((completedCases + (subProgress / 100)) / totalCases) * 100
                    );
                    
                    return {
                      ...prev,
                      progress: overallProgress,
                      subProgress
                    };
                  });
                  
                  if (elapsedTime < validatedConfig.testDuration) {
                    requestAnimationFrame(animateProgress);
                  }
                };
                
                requestAnimationFrame(animateProgress);
              }
            }, validatedConfig.batchInterval);
          });
          
          // Wait for this test to complete
          await runSingleTest();
        } catch (err) {
          setError(t('errors.testFailed', 'Failed to start test. Please try again.'));
          console.error('Error running test case:', err);
        }
      }
      
      // Add all results to history at once
      setTestHistory(prev => [...allResults, ...prev]);
      
    } catch (err) {
      setError(t('errors.testFailed', 'Failed to start test. Please try again.'));
      console.error('Error running test suite:', err);
    } finally {
      isRunningSuiteRef.current = false;
      setIsRunningSuite(false);
      setCurrentSuiteProgress(null);
    }
  };
  
  // Run all tests (a full test suite with all options)
  const runAllTestSuite = () => {
    // Ensure we're using a fresh copy of the options
    const allOptions = { 
      selectedBatchSizes: [...ALL_TEST_OPTIONS.selectedBatchSizes], 
      selectedIntervals: [...ALL_TEST_OPTIONS.selectedIntervals] 
    };
    runTestSuite(allOptions);
  };
  
  // Copy test history to clipboard as CSV
  const copyTestHistoryToClipboard = () => {
    if (testHistory.length === 0) return;
    
    // Create CSV header
    const csvHeader = 'Timestamp,CounterId,Clicks/Sec,Duration,BatchSize,Interval,SuccessRate,AvgResponse,MaxResponse,Throughput,TotalClicks';
    
    // Create CSV rows
    const csvRows = testHistory.map(entry => {
      const timestamp = entry.timestamp ? new Date(entry.timestamp).toLocaleString() : entry.date.toLocaleString();
      const { config, metrics } = entry;
      
      return [
        timestamp,
        config.counterId,
        config.clicksPerSecond,
        config.testDuration,
        config.batchSize,
        config.batchInterval,
        metrics.successRate.toFixed(2) + '%',
        metrics.averageResponseTime.toFixed(2) + 'ms',
        metrics.maxResponseTime.toFixed(2) + 'ms',
        metrics.clicksPerSecond.toFixed(2),
        metrics.totalClicks
      ].join(',');
    });
    
    // Combine header and rows
    const csvContent = [csvHeader, ...csvRows].join('\n');
    
    // Copy to clipboard
    navigator.clipboard.writeText(csvContent)
      .then(() => {
        showToast({ 
          message: t('history.copied', 'Copied to clipboard!'),
          type: 'success' 
        });
      })
      .catch(err => {
        console.error('Failed to copy to clipboard:', err);
        showToast({ 
          message: 'Failed to copy to clipboard', 
          type: 'danger' 
        });
      });
  };
  
  // Cancel in-progress test suite
  const cancelTestSuite = () => {
    if (isRunningSuite) {
      isRunningSuiteRef.current = false;
      setIsRunningSuite(false);
      setCurrentSuiteProgress(null);
      setError(null);
      
      // Clean up any running interval
      if (testIntervalRef.current) {
        clearInterval(testIntervalRef.current);
        testIntervalRef.current = null;
      }
    }
  };
  
  // Apply default settings
  const applyDefaultSettings = () => {
    setTestConfig({
      counterId: testConfig.counterId, // Keep the selected counter
      clicksPerSecond: 10,
      testDuration: 10,
      batchSize: config.batching.size,
      batchInterval: config.batching.interval
    });
  };
  
  // Compute total test cases for the test suite
  const computeTestCaseCount = () => {
    return getTestCaseCount(
      testSuiteConfig.selectedBatchSizes.length, 
      testSuiteConfig.selectedIntervals.length
    );
  };
  
  // Compute estimated time for the test suite
  const computeEstimatedTime = () => {
    return getEstimatedTime(
      testSuiteConfig.selectedBatchSizes.length, 
      testSuiteConfig.selectedIntervals.length, 
      testConfig.testDuration as number
    );
  };
  
  // Clear test history
  const clearTestHistory = () => setTestHistory([]);

  // Wrapper for TestSuiteConfiguration that doesn't take parameters
  const handleRunTestSuite = () => {
    runTestSuite();
  };

  // Wrapper for running all tests without parameters
  const handleRunAllTestSuite = () => {
    runAllTestSuite();
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-3">{t('title', 'Performance Testing')}</h1>
          
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}
          
          <Row className="g-4">
            <Col lg={6} xl={4}>
              <TestConfiguration 
                testConfig={testConfig}
                counters={counters}
                loading={loading}
                onChange={handleConfigChange}
                onStartTest={startTest}
                onStopTest={stopTest}
                isRunningTest={isRunningTest}
                onApplyDefaults={applyDefaultSettings}
              />
            </Col>
            
            <Col lg={6} xl={4}>
              <TestSuiteConfiguration 
                testSuiteConfig={testSuiteConfig}
                isRunningSuite={isRunningSuite}
                isRunningTest={isRunningTest}
                loading={loading}
                hasSelectedCounter={!!testConfig.counterId}
                currentSuiteProgress={currentSuiteProgress}
                handleTestSuiteConfigChange={handleTestSuiteConfigChange}
                runTestSuite={handleRunTestSuite}
                runAllTestSuite={handleRunAllTestSuite}
                cancelTestSuite={cancelTestSuite}
                getTestCaseCount={computeTestCaseCount}
                getEstimatedTime={computeEstimatedTime}
              />
            </Col>
            
            <Col lg={12} xl={4}>
              <PerformanceMetricsComponent metrics={metrics} />
            </Col>
          </Row>
        </Col>
      </Row>
      
      <Row>
        <Col>
          <TestHistory 
            testHistory={testHistory}
            copyTestHistoryToClipboard={copyTestHistoryToClipboard}
            clearTestHistory={clearTestHistory}
          />
        </Col>
      </Row>
      
      <ToastContainer toasts={toasts} />
    </Container>
  );
};

export default Performance; 